<script lang="ts">
  import { onMount, tick } from "svelte";
  import { push } from "svelte-spa-router";
  import { paletteOpen } from "../stores";
  import { searchEntries } from "../search";
  import { entries } from "../content";
  import { categoryById } from "../categories";

  let query = "";
  let inputEl: HTMLInputElement | null = null;
  let activeIndex = 0;

  $: results = query.trim() ? searchEntries($entries, query, 12) : [];

  // Global ⌘K / Ctrl+K shortcut.
  onMount(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        paletteOpen.update((v) => !v);
      } else if (e.key === "Escape") {
        paletteOpen.set(false);
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  // Focus the input when the palette opens.
  $: if ($paletteOpen) {
    activeIndex = 0;
    query = "";
    tick().then(() => inputEl?.focus());
  }

  function close() {
    paletteOpen.set(false);
  }

  function go(slug: string) {
    push(`/sop/${slug}`);
    close();
  }

  function onKeydown(e: KeyboardEvent) {
    if (results.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      activeIndex = (activeIndex + 1) % results.length;
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      activeIndex = (activeIndex - 1 + results.length) % results.length;
    } else if (e.key === "Enter") {
      e.preventDefault();
      go(results[activeIndex].slug);
    }
  }
</script>

{#if $paletteOpen}
  <div
    class="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-start justify-center pt-[15vh] px-4"
    on:click={close}
    on:keydown={(e) => e.key === "Escape" && close()}
    role="presentation"
  >
    <div
      class="w-full max-w-xl panel-raised overflow-hidden shadow-2xl"
      on:click|stopPropagation
      on:keydown|stopPropagation
      role="dialog"
      aria-modal="true"
      aria-label="Search SOPs"
      tabindex="-1"
    >
      <div class="flex items-center gap-3 px-4 py-3 border-b border-border">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" class="text-text-muted">
          <circle cx="11" cy="11" r="7"/>
          <line x1="21" y1="21" x2="16.5" y2="16.5"/>
        </svg>
        <input
          bind:this={inputEl}
          bind:value={query}
          on:keydown={onKeydown}
          type="text"
          placeholder="Search SOPs, cheat sheets, tags..."
          class="flex-1 bg-transparent outline-none text-text placeholder:text-text-dim text-sm"
          autocomplete="off"
        />
        <kbd class="text-[10px] px-1.5 py-0.5 rounded bg-bg-panel border border-border text-text-dim">esc</kbd>
      </div>

      <div class="max-h-[50vh] overflow-y-auto">
        {#if query.trim() === ""}
          <div class="px-4 py-6 text-sm text-text-dim text-center">
            Start typing to search
          </div>
        {:else if results.length === 0}
          <div class="px-4 py-6 text-sm text-text-dim text-center">
            No matches for "{query}"
          </div>
        {:else}
          <ul>
            {#each results as r, i}
              {@const cat = categoryById(r.category)}
              <li>
                <button
                  class="w-full text-left px-4 py-2.5 flex items-center justify-between gap-3
                         {i === activeIndex ? 'bg-bg-raised' : 'hover:bg-bg-panel'}
                         border-b border-border last:border-b-0"
                  on:click={() => go(r.slug)}
                  on:mouseenter={() => (activeIndex = i)}
                >
                  <div class="min-w-0">
                    <div class="text-sm text-text truncate">{r.title}</div>
                    {#if r.description}
                      <div class="text-xs text-text-dim truncate">{r.description}</div>
                    {/if}
                  </div>
                  <div class="flex items-center gap-2 shrink-0">
                    {#if cat}
                      <span class="pill text-text-muted text-[10px] px-2 py-0.5">{cat.label}</span>
                    {/if}
                    {#if r.type === "cheat-sheet"}
                      <span class="pill bg-accent-ghost border-accent text-accent text-[10px] px-2 py-0.5">Cheat</span>
                    {/if}
                  </div>
                </button>
              </li>
            {/each}
          </ul>
        {/if}
      </div>

      <div class="px-4 py-2 border-t border-border flex items-center gap-3 text-[10px] text-text-dim">
        <span><kbd class="px-1 py-0.5 bg-bg-panel border border-border rounded">↑↓</kbd> navigate</span>
        <span><kbd class="px-1 py-0.5 bg-bg-panel border border-border rounded">↵</kbd> open</span>
        <span><kbd class="px-1 py-0.5 bg-bg-panel border border-border rounded">esc</kbd> close</span>
      </div>
    </div>
  </div>
{/if}
